import React, { useState } from 'react';
import axios from 'axios';
import { PaperPlaneTilt, ChatCircle, CheckCircle } from '@phosphor-icons/react';
import { useWorkerLang } from '../../contexts/WorkerLanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactFormSection = () => {
  const { lang } = useWorkerLang();
  const L = (en, es) => (lang === 'es' ? es : en);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [error, setError] = useState('');

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setStatus('done');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || L('Something went wrong — try again', 'Algo salió mal — intenta de nuevo'));
    }
  };

  const inputCls = 'w-full h-12 px-3 bg-white border-2 border-black rounded-none font-medium focus:outline-none focus:ring-2 focus:ring-[#D95800]';

  return (
    <div className="bg-[#0F172A] py-16 border-t-2 border-black" data-testid="landing-contact-section">
      <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-start">
        <div>
          <div className="w-14 h-14 bg-[#D95800] border-2 border-white flex items-center justify-center mb-5">
            <ChatCircle size={30} weight="bold" className="text-white" />
          </div>
          <h2 className="text-3xl font-black text-white uppercase leading-tight mb-3">
            {L('Questions? Send us a message', '¿Preguntas? Envíanos un mensaje')}
          </h2>
          <p className="text-neutral-400 leading-relaxed mb-4">
            {L("Whether you're a homeowner planning a project or a pro looking for work, our team replies within 24 hours.",
               'Ya seas propietario planeando un proyecto o un profesional buscando trabajo, nuestro equipo responde en menos de 24 horas.')}
          </p>
          <a href="mailto:support@constructhelp.com" className="text-[#D95800] font-bold hover:underline">
            support@constructhelp.com
          </a>
        </div>

        {status === 'done' ? (
          <div className="bg-emerald-50 border-2 border-black p-8 text-center shadow-[5px_5px_0px_#D95800]" data-testid="landing-contact-success">
            <CheckCircle size={44} weight="fill" className="mx-auto text-emerald-600 mb-3" />
            <p className="font-black uppercase text-emerald-800">{L('Message sent!', '¡Mensaje enviado!')}</p>
            <p className="text-sm text-emerald-700 mt-1">{L("We'll get back to you within 24 hours.", 'Te responderemos en menos de 24 horas.')}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border-2 border-black p-6 shadow-[5px_5px_0px_#D95800] space-y-4" data-testid="landing-contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input name="name" required value={form.name} onChange={onChange}
                placeholder={L('Your name *', 'Tu nombre *')} className={inputCls} data-testid="landing-contact-name" />
              <input name="email" type="email" required value={form.email} onChange={onChange}
                placeholder={L('Your email *', 'Tu correo *')} className={inputCls} data-testid="landing-contact-email" />
            </div>
            <input name="subject" required value={form.subject} onChange={onChange}
              placeholder={L('Subject *', 'Asunto *')} className={inputCls} data-testid="landing-contact-subject" />
            <textarea name="message" required value={form.message} onChange={onChange} rows={4}
              placeholder={L('Tell us more… *', 'Cuéntanos más… *')}
              className="w-full px-3 py-3 bg-white border-2 border-black rounded-none font-medium focus:outline-none focus:ring-2 focus:ring-[#D95800] resize-none"
              data-testid="landing-contact-message" />
            {status === 'error' && <p className="text-sm font-bold text-red-600" data-testid="landing-contact-error">{error}</p>}
            <button type="submit" disabled={status === 'loading'}
              className="w-full h-13 py-3 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="landing-contact-submit">
              <PaperPlaneTilt size={20} weight="bold" />
              {status === 'loading' ? L('Sending…', 'Enviando…') : L('Send Message', 'Enviar Mensaje')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactFormSection;
